import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  IconButton,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../utils/Api";
import {
  calculateTaxAmount,
  getProductTaxPercent,
  toMoneyNumber,
} from "../utils/tax";

const COLORS = {
  primary: "#09A5F7",
  primaryHover: "#008FD8",
  primaryText: "#0E4961",
  secondaryText: "#8A8A8A",
  mutedText: "#9CA3AF",
  border: "#7EC8EA",
  white: "#FFFFFF",
  error: "#E53935",
  closeIcon: "#009688",
};

const formatCurrency = (value) => `₹ ${Number(value || 0)}`;

const formatAddonPrice = (price) => {
  const amount = Number(price || 0);

  if (amount <= 0) {
    return "";
  }

  return ` +₹${amount}`;
};

const getId = (item) => {
  if (!item) {
    return null;
  }

  if (typeof item === "string") {
    return item;
  }

  return item?._id || item?.value || null;
};

const getSelectedValueId = (addonValue) => {
  if (addonValue?.value && typeof addonValue.value === "object") {
    return getId(addonValue.value) || addonValue?._id || null;
  }

  return addonValue?._id || null;
};

const getSelectedValueName = (addonValue) => {
  if (typeof addonValue?.value === "object") {
    return (
      addonValue?.value?.value ||
      addonValue?.value?.label ||
      addonValue?.value?.name ||
      addonValue?.label ||
      addonValue?.subLabel ||
      "Addon"
    );
  }

  return (
    addonValue?.label || addonValue?.value || addonValue?.subLabel || "Addon"
  );
};

const getAddonValueLabel = (addonValue) => {
  if (typeof addonValue?.value === "object") {
    return (
      addonValue?.value?.value ||
      addonValue?.value?.label ||
      addonValue?.value?.name ||
      addonValue?.subLabel ||
      "Addon"
    );
  }

  return addonValue?.value || addonValue?.subLabel || "Addon";
};

const getProductPrice = (currentProduct) =>
  Number(
    currentProduct?.newOfferPrice ||
    currentProduct?.offerPrice ||
    currentProduct?.price ||
    0,
  );

const getProductMrpRate = (currentProduct) =>
  Number(
    currentProduct?.newMrp ??
    currentProduct?.mrpRate ??
    currentProduct?.mrp ??
    currentProduct?.defaultMrp ??
    currentProduct?.price ??
    getProductPrice(currentProduct),
  );

const getProductImage = (currentProduct, fallbackImage) =>
  currentProduct?.displayImageUrl ||
  currentProduct?.displayImage ||
  currentProduct?.imageUrl ||
  currentProduct?.image ||
  fallbackImage ||
  null;

const getPreparationTime = (currentProduct) =>
  currentProduct?.makingTime ||
  currentProduct?.preparationTime ||
  currentProduct?.preparation_time ||
  currentProduct?.prepTime ||
  null;

const CustomizationModal = ({
  open,
  cartItem,
  outletData,
  anchorRef,
  initialAnchorRect = null,
  onClose,
  onExited,
  onProceed,
  onMessage,
}) => {
  const addonGroupRefs = useRef({});

  const [product, setProduct] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState({});
  const [missingRequiredAddonId, setMissingRequiredAddonId] = useState(null);
  const [exhaustedAddonId, setExhaustedAddonId] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [saving, setSaving] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);

  const initializedKeyRef = useRef(null);

  const outletId = outletData?._id || cartItem?.outletId || null;

  const getAddonValueStatus = (addonValue) => {
    const populatedValue =
      typeof addonValue?.value === "object" ? addonValue.value : {};

    return addonValue?.status || populatedValue?.status || "ACTIVE";
  };

  const isOutletExcluded = (excludedOutlets = []) => {
    if (!Array.isArray(excludedOutlets) || !outletId) {
      return false;
    }

    return excludedOutlets.some((outlet) => getId(outlet) === outletId);
  };

  const normalizeExcludedValues = (excludedValues = []) => {
    if (!Array.isArray(excludedValues)) {
      return [];
    }

    return excludedValues.map((value) => getId(value)).filter(Boolean);
  };

  const normalizeOutletIds = (outlets = []) =>
    (Array.isArray(outlets) ? outlets : [])
      .map((outlet) => getId(outlet))
      .filter(Boolean);

  const isDefaultAddonValue = (addonValue) => {
    const populatedValue =
      typeof addonValue?.value === "object" ? addonValue.value : {};

    return (
      addonValue?.isDefault === true ||
      addonValue?.default === true ||
      addonValue?.isDefaultValue === true ||
      addonValue?.selectedByDefault === true ||
      addonValue?.isSelected === true ||
      populatedValue?.isDefault === true ||
      populatedValue?.default === true ||
      populatedValue?.isDefaultValue === true ||
      populatedValue?.selectedByDefault === true ||
      populatedValue?.isSelected === true
    );
  };

  const isAddonValueExhausted = (addonValue) => {
    const populatedValue =
      typeof addonValue?.value === "object" ? addonValue.value : {};

    return Boolean(
      addonValue?.isExhausted ||
      addonValue?.isOutOfStock ||
      addonValue?.outOfStock ||
      addonValue?.isAvailable === false ||
      populatedValue?.isExhausted ||
      populatedValue?.isOutOfStock ||
      populatedValue?.outOfStock ||
      populatedValue?.isAvailable === false,
    );
  };

  const isAddonValueInactive = (addonValue) =>
    getAddonValueStatus(addonValue) === "INACTIVE";

  const hasAddonValueIconInfo = (addonValue) => {
    const populatedValue =
      typeof addonValue?.value === "object" ? addonValue.value : {};

    return Boolean(addonValue?.iconInfo || populatedValue?.iconInfo);
  };

  const isAddonValueOutletExcluded = (addonValue) => {
    const populatedValue =
      typeof addonValue?.value === "object" ? addonValue.value : {};

    return (
      isOutletExcluded(addonValue?.excludedOutlets || []) ||
      isOutletExcluded(populatedValue?.excludedOutlets || [])
    );
  };

  const getAddonValueOrderType = (addonValue) => {
    const populatedValue =
      typeof addonValue?.value === "object" ? addonValue.value : {};

    return String(addonValue?.orderType || populatedValue?.orderType || "ALL")
      .trim()
      .toUpperCase();
  };

  const isAddonValueAllowedForPickup = (addonValue) => {
    const orderType = getAddonValueOrderType(addonValue);

    return orderType === "ALL" || orderType === "PICKUP";
  };

  const visibleAddons = useMemo(() => {
    const productAddons = Array.isArray(product?.addons) ? product.addons : [];

    const addons = productAddons
      .filter((allocation) => {
        if (allocation?.status === "INACTIVE") {
          return false;
        }

        return !isOutletExcluded(allocation?.excludedOutlets || []);
      })
      .map((allocation) => {
        const addonMaster =
          allocation?.addon && typeof allocation.addon === "object"
            ? allocation.addon
            : allocation;

        const excludedValueIds = normalizeExcludedValues(
          allocation?.excludedValues || [],
        );

        const allocationValues = Array.isArray(allocation?.values)
          ? allocation.values
          : [];

        const values = allocationValues
          .filter((valueItem) => {
            if (hasAddonValueIconInfo(valueItem)) {
              return false;
            }

            if (!isAddonValueAllowedForPickup(valueItem)) {
              return false;
            }

            const isDefault = isDefaultAddonValue(valueItem);
            const isInactive = isAddonValueInactive(valueItem);
            const isValueOutletExcluded =
              isAddonValueOutletExcluded(valueItem);
            const valueId = getId(valueItem?.value);
            const isInExcludedValues = Boolean(
              valueId && excludedValueIds.includes(valueId),
            );

            return (
              (!isInactive || isDefault) &&
              (!isValueOutletExcluded || isDefault) &&
              (!isInExcludedValues || isDefault)
            );
          })
          .map((valueItem) => {
            const isDefault = isDefaultAddonValue(valueItem);
            const isInactive = isAddonValueInactive(valueItem);
            const isValueOutletExcluded =
              isAddonValueOutletExcluded(valueItem);
            const valueId = getId(valueItem?.value);
            const isInExcludedValues = Boolean(
              valueId && excludedValueIds.includes(valueId),
            );

            return {
              ...valueItem,
              label: getAddonValueLabel(valueItem),
              price: Number(valueItem?.price || 0),
              status: getAddonValueStatus(valueItem),
              isDefault,
              isExhausted: isAddonValueExhausted(valueItem),
              isInactive,
              isOutletExcluded: isValueOutletExcluded,
              isInExcludedValues,
              isDisabled:
                isValueOutletExcluded ||
                isInExcludedValues ||
                (isDefault && isInactive),
            };
          });

        return {
          ...allocation,
          title: addonMaster?.title || allocation?.title || "Choose addon",
          isOptional: addonMaster?.isOptional ?? allocation?.isOptional ?? true,
          isMultipleSelect:
            addonMaster?.isMultipleSelect ??
            allocation?.isMultipleSelect ??
            false,
          values,
        };
      })
      .filter((addon) =>
        addon.values.some(
          (addonValue) =>
            !addonValue?.isInactive &&
            !addonValue?.isOutletExcluded &&
            !addonValue?.isInExcludedValues,
        ),
      );

    return [...addons].sort((firstAddon, secondAddon) => {
      const firstTitle = String(firstAddon?.title || "")
        .trim()
        .toLowerCase();
      const secondTitle = String(secondAddon?.title || "")
        .trim()
        .toLowerCase();
      const firstIsSize = firstTitle === "size" || firstTitle === "cup size";
      const secondIsSize =
        secondTitle === "size" || secondTitle === "cup size";

      if (firstIsSize === secondIsSize) {
        return 0;
      }

      return firstIsSize ? -1 : 1;
    });
  }, [product, outletId]);

  const prepareSelectedAddonValue = (addon, addonValue) => ({
    ...addonValue,
    addonAllocationId: addon?._id,
    addonId: getId(addon?.addon) || addon?._id,
    addonTitle: addon?.title,
    addonExcludedOutlets: normalizeOutletIds(addon?.excludedOutlets || []),
    excludedOutlets: normalizeOutletIds(addonValue?.excludedOutlets || []),
    status: getAddonValueStatus(addonValue),
    isInactive: Boolean(addonValue?.isInactive),
    isOutletExcluded: Boolean(addonValue?.isOutletExcluded),
    isInExcludedValues: Boolean(addonValue?.isInExcludedValues),
    isDisabled: Boolean(addonValue?.isDisabled),
    isExhausted: isAddonValueExhausted(addonValue),
  });

  const buildDefaultAddonSelections = (addons = []) =>
    addons.reduce((selections, addon) => {
      const defaultValues = (addon?.values || []).filter(
        (addonValue) =>
          isDefaultAddonValue(addonValue) &&
          !addonValue?.isExhausted &&
          !addonValue?.isInactive &&
          !addonValue?.isOutletExcluded &&
          !addonValue?.isInExcludedValues,
      );

      if (defaultValues.length === 0) {
        return selections;
      }

      const valuesToSelect = addon?.isMultipleSelect
        ? defaultValues
        : defaultValues.slice(0, 1);

      selections[addon?._id] = valuesToSelect.map((addonValue) =>
        prepareSelectedAddonValue(addon, addonValue),
      );

      return selections;
    }, {});

  const matchesCartAddon = (addon, addonValue, cartAddon) => {
    const allocationId = String(
      cartAddon?.addonAllocationId || cartAddon?.addonId || "",
    );
    const addonId = String(getId(addon?.addon) || addon?._id || "");
    const matchesGroup =
      allocationId === String(addon?._id || "") || allocationId === addonId;

    if (!matchesGroup && allocationId) {
      return false;
    }

    const selectedValueId = String(
      cartAddon?.selectedValueId || cartAddon?.valueId || cartAddon?._id || "",
    );
    const valueId = String(getSelectedValueId(addonValue) || "");
    const cartLabel = String(
      cartAddon?.label || cartAddon?.selectedValue || "",
    ).trim();
    const valueLabel = String(addonValue?.label || "").trim();

    return (
      selectedValueId === String(addonValue?._id || "") ||
      selectedValueId === valueId ||
      (cartLabel && valueLabel && cartLabel === valueLabel)
    );
  };

  const buildSelectionsFromCart = (addons = []) => {
    const cartAddons = Array.isArray(cartItem?.addons) ? cartItem.addons : [];
    const defaults = buildDefaultAddonSelections(addons);

    if (cartAddons.length === 0) {
      return defaults;
    }

    const selections = addons.reduce((currentSelections, addon) => {
      const matchedValues = (addon?.values || []).filter((addonValue) =>
        cartAddons.some((cartAddon) =>
          matchesCartAddon(addon, addonValue, cartAddon),
        ),
      );

      if (matchedValues.length === 0) {
        return currentSelections;
      }

      const valuesToSelect = addon?.isMultipleSelect
        ? matchedValues
        : matchedValues.slice(0, 1);

      currentSelections[addon?._id] = valuesToSelect.map((addonValue) =>
        prepareSelectedAddonValue(addon, addonValue),
      );

      return currentSelections;
    }, {});

    return {
      ...defaults,
      ...selections,
    };
  };

  const selectedAddonValues = useMemo(
    () =>
      Object.values(selectedAddons).flatMap((values) =>
        Array.isArray(values) ? values : [],
      ),
    [selectedAddons],
  );

  const addonsTotal = useMemo(
    () =>
      toMoneyNumber(
        selectedAddonValues.reduce(
          (total, addonValue) => total + Number(addonValue?.price || 0),
          0,
        ),
      ),
    [selectedAddonValues],
  );

  const currentItemUnitPrice = useMemo(
    () =>
      toMoneyNumber(
        Number(cartItem?.productPrice ?? getProductPrice(product)) +
        addonsTotal,
      ),
    [cartItem?.productPrice, product, addonsTotal],
  );

  const productTaxPercent = useMemo(
    () =>
      Number(
        cartItem?.productTaxPercent || getProductTaxPercent(product) || 0,
      ),
    [cartItem?.productTaxPercent, product],
  );

  const currentItemTotal = useMemo(
    () =>
      calculateTaxAmount(currentItemUnitPrice, quantity, productTaxPercent)
        .taxable,
    [currentItemUnitPrice, quantity, productTaxPercent],
  );

  const isAddonValueSelected = (addonAllocationId, addonValueId) => {
    const values = selectedAddons?.[addonAllocationId] || [];

    return values.some((value) => value?._id === addonValueId);
  };

  const handleAddonSelection = (addon, addonValue) => {
    const existingValues = selectedAddons?.[addon?._id] || [];
    const alreadySelected = existingValues.some(
      (value) => value?._id === addonValue?._id,
    );

    if (addonValue?.isDisabled && !alreadySelected) {
      onMessage?.(
        addonValue?.isInactive
          ? "This default addon is currently unavailable."
          : "This addon is not available at this outlet.",
        "warning",
      );

      return;
    }

    if (addonValue?.isExhausted && !alreadySelected) {
      return;
    }

    setMissingRequiredAddonId((currentMissingId) =>
      currentMissingId === addon?._id ? null : currentMissingId,
    );

    setExhaustedAddonId((currentExhaustedId) =>
      currentExhaustedId === addon?._id ? null : currentExhaustedId,
    );

    setSelectedAddons((currentSelections) => {
      const currentValues = currentSelections?.[addon?._id] || [];
      const isAlreadySelected = currentValues.some(
        (value) => value?._id === addonValue?._id,
      );

      if (addon?.isMultipleSelect) {
        return {
          ...currentSelections,
          [addon?._id]: isAlreadySelected
            ? currentValues.filter((value) => value?._id !== addonValue?._id)
            : [...currentValues, prepareSelectedAddonValue(addon, addonValue)],
        };
      }

      if (isAlreadySelected && addon?.isOptional) {
        return {
          ...currentSelections,
          [addon?._id]: [],
        };
      }

      return {
        ...currentSelections,
        [addon?._id]: [prepareSelectedAddonValue(addon, addonValue)],
      };
    });
  };

  const scrollToAddonGroup = (addonId) => {
    window.requestAnimationFrame(() => {
      addonGroupRefs.current[addonId]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  };

  const validateRequiredAddons = () => {
    const exhaustedAddon = visibleAddons.find((addon) =>
      (selectedAddons?.[addon?._id] || []).some((value) => value?.isExhausted),
    );

    if (exhaustedAddon) {
      setMissingRequiredAddonId(null);
      setExhaustedAddonId(exhaustedAddon?._id);
      scrollToAddonGroup(exhaustedAddon?._id);
      onMessage?.(
        `Please select another value for ${exhaustedAddon?.title || "this addon"
        }.`,
        "warning",
      );

      return false;
    }

    const missingAddon = visibleAddons.find((addon) => {
      if (addon?.isOptional) {
        return false;
      }

      return (selectedAddons?.[addon?._id] || []).length === 0;
    });

    if (!missingAddon) {
      setMissingRequiredAddonId(null);
      setExhaustedAddonId(null);

      return true;
    }

    setExhaustedAddonId(null);
    setMissingRequiredAddonId(missingAddon?._id);
    scrollToAddonGroup(missingAddon?._id);
    onMessage?.(`Please select ${missingAddon?.title}.`, "warning");

    return false;
  };

  const resetCustomizationState = () => {
    initializedKeyRef.current = null;
    setProduct(null);
    setSelectedAddons({});
    setMissingRequiredAddonId(null);
    setExhaustedAddonId(null);
    setQuantity(1);
    setSaving(false);
  };

  const handleClose = () => {
    onClose?.();
  };

  const handleExited = () => {
    resetCustomizationState();
    onExited?.();
  };

  const handleProceed = () => {
    if (detailsLoading || !product?._id) {
      return;
    }

    if (visibleAddons.length === 0) {
      handleClose();

      return;
    }

    if (!validateRequiredAddons()) {
      return;
    }

    try {
      setSaving(true);

      const addons = selectedAddonValues.map((addonValue) => {
        const label = addonValue?.label || getSelectedValueName(addonValue);
        const selectedValueId = getSelectedValueId(addonValue);

        return {
          _id: addonValue?._id,
          valueId: selectedValueId,
          selectedValue: label,
          selectedValueId,
          addonId: addonValue?.addonId,
          addonAllocationId:
            addonValue?.addonAllocationId || addonValue?.addonId || null,
          addonTitle: addonValue?.addonTitle,
          label,
          price: Number(addonValue?.price || 0),
          subLabel: addonValue?.subLabel || null,
          excludedOutlets: addonValue?.excludedOutlets || [],
          addonExcludedOutlets: addonValue?.addonExcludedOutlets || [],
        };
      });

      const productPrice = Number(
        cartItem?.productPrice ?? getProductPrice(product),
      );
      const mrpRate = Number(cartItem?.mrpRate ?? getProductMrpRate(product));
      const addonPrice = toMoneyNumber(
        addons.reduce((total, addon) => total + Number(addon?.price || 0), 0),
      );
      const unitPrice = toMoneyNumber(productPrice + addonPrice);

      onProceed?.(cartItem?.cartItemId, {
        productId: product._id || cartItem?.productId,
        productName: product?.name || cartItem?.productName,
        productImage:
          getProductImage(product, cartItem?.productImage) ||
          cartItem?.productImage,
        categoryId: product?.category?._id || cartItem?.categoryId || null,
        categoryName:
          product?.category?.name || cartItem?.categoryName || null,
        productPrice,
        mrpRate,
        addons,
        addonPrice,
        unitPrice,
        quantity,
        productTaxPercent,
        makingTime: Number(
          getPreparationTime(product) || cartItem?.makingTime || 20,
        ),
        menuGroup:
          product?.menuGroup?._id ||
          product?.menuGroup ||
          product?.menuGroupId ||
          cartItem?.menuGroup ||
          null,
        hasAddon: true,
        outletId: outletId || cartItem?.outletId || null,
      });

      handleClose();
    } catch (error) {
      console.error("Unable to update customization:", error);
      onMessage?.("Unable to update item customization.", "error");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!open || !cartItem?.productId || !outletId) {
      return undefined;
    }

    let cancelled = false;
    const abortController = new AbortController();

    const fetchProductDetails = async () => {
      setProduct(null);
      setDetailsLoading(true);

      try {
        const response = await axios.get(
          `${API.BASE_URL}product/get-product-category-addon-details/${cartItem.productId}/${outletId}/PICKUP`,
          {
            signal: abortController.signal,
          },
        );

        const responseData = response?.data?.data || response?.data || {};
        const fetchedProduct =
          responseData?.product || (responseData?._id ? responseData : null);

        if (cancelled) {
          return;
        }

        if (!fetchedProduct?._id) {
          onMessage?.("Unable to fetch product details.", "error");
          onClose?.();
          return;
        }

        setProduct({
          ...fetchedProduct,
          name: fetchedProduct?.name || cartItem?.productName,
        });
      } catch (error) {
        if (
          cancelled ||
          error?.code === "ERR_CANCELED" ||
          error?.name === "CanceledError" ||
          axios.isCancel?.(error)
        ) {
          return;
        }

        console.error(
          "Error fetching product details:",
          error?.response?.data || error,
        );

        onMessage?.(
          error?.response?.data?.message || "Unable to fetch product details.",
          "error",
        );

        onClose?.();
      } finally {
        if (!cancelled) {
          setDetailsLoading(false);
        }
      }
    };

    fetchProductDetails();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [open, cartItem?.productId, cartItem?.cartItemId, outletId]);

  useEffect(() => {
    if (!open) {
      initializedKeyRef.current = null;
      return;
    }

    if (!product?._id || detailsLoading) {
      return;
    }

    const initKey = `${cartItem?.cartItemId || ""}-${product._id}`;

    if (initializedKeyRef.current === initKey) {
      return;
    }

    if (visibleAddons.length === 0) {
      onMessage?.("No customization available for this item.", "warning");
      handleClose();

      return;
    }

    initializedKeyRef.current = initKey;
    setMissingRequiredAddonId(null);
    setExhaustedAddonId(null);
    setQuantity(Math.max(Number(cartItem?.quantity || 1), 1));
    setSaving(false);
    addonGroupRefs.current = {};
    setSelectedAddons(buildSelectionsFromCart(visibleAddons));
  }, [open, product?._id, visibleAddons, detailsLoading, cartItem?.cartItemId]);

  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }

    const element = anchorRef?.current;

    if (
      element &&
      window.innerHeight - element.getBoundingClientRect().top < 300
    ) {
      element.scrollIntoView({
        block: "center",
        behavior: "auto",
      });
    }

    const measureAnchor = () => {
      const anchorElement = anchorRef?.current;

      if (!anchorElement) {
        setAnchorRect(null);
        return;
      }

      const nextRect = anchorElement.getBoundingClientRect();

      setAnchorRect({
        top: nextRect.top,
        left: nextRect.left,
        width: nextRect.width,
        height: nextRect.height,
      });
    };

    measureAnchor();
    window.addEventListener("resize", measureAnchor);

    return () => {
      window.removeEventListener("resize", measureAnchor);
    };
  }, [open, anchorRef]);

  const productName = product?.name || cartItem?.productName || "Product";
  const headerDescription =
    selectedAddonValues
      .map((addonValue) => addonValue?.label)
      .filter(Boolean)
      .join(", ") ||
    (Array.isArray(cartItem?.addons) ? cartItem.addons : [])
      .map(
        (addon) =>
          addon?.label ||
          addon?.selectedValue ||
          addon?.name ||
          addon?.addonTitle,
      )
      .filter(Boolean)
      .join(", ") ||
    cartItem?.categoryName ||
    "";

  const viewportPadding = 12;
  const resolvedAnchorRect = anchorRect || initialAnchorRect;
  const panelTop = resolvedAnchorRect
    ? Math.max(viewportPadding, resolvedAnchorRect.top)
    : "50%";
  const panelLeft = resolvedAnchorRect ? resolvedAnchorRect.left : "50%";
  const panelWidth = resolvedAnchorRect?.width || 420;
  const panelMaxHeight = resolvedAnchorRect
    ? Math.max(
      window.innerHeight - resolvedAnchorRect.top - viewportPadding,
      240,
    )
    : "78dvh";

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      disableScrollLock
      disableEnforceFocus
      disableAutoFocus
      BackdropProps={{
        timeout: 250,
        sx: {
          backgroundColor: "rgba(15, 23, 42, 0.45)",
        },
      }}
    >
      <Fade in={open} timeout={250} onExited={handleExited}>
        <Box
          tabIndex={-1}
          sx={{
            position: "absolute",
            top: panelTop,
            left: panelLeft,
            width: panelWidth,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: panelMaxHeight,
            outline: "none",
            transform: resolvedAnchorRect ? "none" : "translate(-50%, -50%)",
          }}
        >
          <Box
            component={motion.div}
            initial={{
              height: resolvedAnchorRect?.height || 92,
              opacity: 0.96,
            }}
            animate={{
              height: "auto",
              opacity: 1,
            }}
            transition={{
              duration: 0.32,
              ease: [0.22, 1, 0.36, 1],
            }}
            sx={{
              overflow: "hidden",
              maxHeight: panelMaxHeight,
              display: "flex",
              flexDirection: "column",
              borderRadius: "22px",
              backgroundColor: COLORS.white,
              boxShadow: "0 18px 48px rgba(14, 73, 97, 0.22)",
              transformOrigin: "top center",
            }}
          >
            <Box
              className="custom-scrollbar"
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                px: 2,
                pt: 1.8,
                pb: 1.2,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={3}
              >
                <Box
                  sx={{
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 600,
                      lineHeight: 1.2,
                      color: "#1A1A1A",
                    }}
                  >
                    {productName}
                  </Typography>

                  {headerDescription && (
                    <Typography
                      sx={{
                        mt: 0.35,
                        fontSize: 10,
                        lineHeight: 1.35,
                        color: COLORS.mutedText,
                      }}
                    >
                      {headerDescription}
                    </Typography>
                  )}
                </Box>

                <QuantitySelector
                  quantity={quantity}
                  onDecrease={() =>
                    setQuantity((current) => Math.max(1, current - 1))
                  }
                  onIncrease={() => setQuantity((current) => current + 1)}
                />

                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: COLORS.primary,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {formatCurrency(currentItemTotal)}
                </Typography>
              </Stack>

              {detailsLoading && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 5,
                  }}
                >
                  <CircularProgress
                    size={28}
                    sx={{
                      color: COLORS.primary,
                    }}
                  />
                </Box>
              )}

              {!detailsLoading && visibleAddons.length > 0 && (
                <Stack
                  spacing={2.1}
                  sx={{
                    mt: 2.2,
                  }}
                >
                  {visibleAddons.map((addon) => (
                    <Box
                      key={addon?._id}
                      ref={(element) => {
                        addonGroupRefs.current[addon?._id] = element;
                      }}
                    >
                      <AddonGroup
                        addon={addon}
                        isSelected={isAddonValueSelected}
                        onSelect={handleAddonSelection}
                        showValidationError={
                          missingRequiredAddonId === addon?._id
                        }
                        showExhaustedError={exhaustedAddonId === addon?._id}
                      />
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                px: 2,
                pt: 0.4,
                pb: 1.6,
              }}
            >
              <Button
                fullWidth
                variant="contained"
                disabled={
                  detailsLoading || saving || visibleAddons.length === 0
                }
                onClick={handleProceed}
                sx={{
                  minHeight: 30,
                  borderRadius: "999px",
                  backgroundColor: COLORS.primary,
                  color: COLORS.white,
                  boxShadow: "none",
                  fontSize: 16,
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: COLORS.primaryHover,
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#A7DDF5",
                    color: COLORS.white,
                  },
                }}
              >
                {saving ? (
                  <CircularProgress
                    size={20}
                    sx={{
                      color: COLORS.white,
                    }}
                  />
                ) : (
                  "Proceed"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

const AddonGroup = ({
  addon,
  isSelected,
  onSelect,
  showValidationError,
  showExhaustedError,
}) => {
  const title = String(addon?.title || "").trim();
  const isSizeAddon =
    title.toLowerCase() === "size" || title.toLowerCase() === "cup size";
  const displayTitle = isSizeAddon ? "Cup Size" : addon?.title;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="baseline"
        spacing={0.7}
        sx={{
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: COLORS.primaryText,
          }}
        >
          {displayTitle}
        </Typography>

        {!addon?.isOptional && (
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 500,
              color: COLORS.error,
            }}
          >
            (Any One)
          </Typography>
        )}
      </Stack>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {(addon?.values || []).map((addonValue) => {
          const selected = isSelected(addon?._id, addonValue?._id);
          const isUnavailable = Boolean(
            addonValue?.isDisabled || addonValue?.isExhausted,
          );
          const extraPrice = formatAddonPrice(addonValue?.price);

          return (
            <Box
              key={addonValue?._id}
              role={addon?.isMultipleSelect ? "checkbox" : "radio"}
              aria-checked={selected}
              aria-disabled={isUnavailable && !selected}
              tabIndex={0}
              onClick={() => onSelect(addon, addonValue)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelect(addon, addonValue);
                }
              }}
              sx={{
                px: 1.7,
                minHeight: 28,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "999px",
                cursor: isUnavailable && !selected ? "not-allowed" : "pointer",
                opacity: isUnavailable ? 0.45 : 1,
                backgroundColor: selected ? COLORS.primary : COLORS.white,
                border: `1.5px solid ${COLORS.primary}`,
                color: selected ? COLORS.white : COLORS.primaryText,
                outline: "none",
                "&:focus-visible": {
                  boxShadow: "0 0 0 3px rgba(9,165,247,0.22)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: 1.2,
                  color: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                {addonValue?.label || "Option"}
                {extraPrice}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {(showValidationError || showExhaustedError) && (
        <Typography
          sx={{
            mt: 0.8,
            fontSize: 10,
            fontWeight: 500,
            color: COLORS.error,
          }}
        >
          {showExhaustedError
            ? `Please select another available option for ${addon?.title || "this addon"
            }.`
            : `Please select ${addon?.title || "an option"}.`}
        </Typography>
      )}
    </Box>
  );
};

const QuantitySelector = ({ quantity, onDecrease, onIncrease }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={0.7}
    onPointerDown={(event) => event.stopPropagation()}
  >
    <IconButton
      size="small"
      onClick={onDecrease}
      disabled={quantity <= 1}
      aria-label="Decrease quantity"
      sx={{
        width: 20,
        height: 20,
        backgroundColor: COLORS.primary,
        color: COLORS.white,
        "&:hover": {
          backgroundColor: COLORS.primaryHover,
        },
        "&.Mui-disabled": {
          backgroundColor: "#A7DDF5",
          color: COLORS.white,
        },
      }}
    >
      <RemoveIcon
        sx={{
          fontSize: 16,
        }}
      />
    </IconButton>

    <Typography
      sx={{
        minWidth: 14,
        textAlign: "center",
        fontSize: 20,
        fontWeight: 600,
        color: "#1A1A1A",
      }}
    >
      {quantity}
    </Typography>

    <IconButton
      size="small"
      onClick={onIncrease}
      aria-label="Increase quantity"
      sx={{
        width: 20,
        height: 20,
        backgroundColor: COLORS.primary,
        color: COLORS.white,
        "&:hover": {
          backgroundColor: COLORS.primaryHover,
        },
      }}
    >
      <AddIcon
        sx={{
          fontSize: 16,
        }}
      />
    </IconButton>
  </Stack>
);

export default CustomizationModal;
